import type { ClassificationResult } from '@classification-testing/domain';
import { ContentType } from '@classification-testing/domain';
import { getContentTypeIcon } from '@classification-testing/utils';
import { getLanguageDisplayInfo, getLanguageName } from '@classification-testing/utils/languageHelpers';
import { formatTimestamp } from '@core/utils';
import { IconButton, Eye, IconButtonSize, IconSize, StatusCircleBlock, Icon, Badge, BadgeSize, BadgeTextSize, Dialog } from '@soteria/design-system-ve';

import * as styles from './ClassificationResultDialog.css';
import { ContentDisplaySection, ImageDisplaySection } from './ClassificationResultDialogContentSections';
import { PoliciesSection } from './PolicyComponents';

// Type for classification label (from ClassificationResult interface)
type ClassificationLabel = ClassificationResult['labels'][0];

interface ClassificationResultDialogProps {
  result: ClassificationResult;
  isOpen: boolean;
  onClose: () => void;
  isSafe: boolean;
  isContentBlurred: boolean;
  onToggleBlur: () => void;
}

// Helper function to get dialog title based on content type
function getDialogTitle(contentType: ContentType) {
  switch (contentType) {
    case ContentType.AUDIO:
      return 'Audio Classification Result';
    case ContentType.IMAGE:
      return 'Image Classification Result';
    case ContentType.TEXT:
      return 'Text Classification Result';
    case ContentType.MIXED:
      return 'Mixed Content Classification Result';
    default:
      return 'Classification Result';
  }
}

// Helper to normalize content type for case-insensitive comparison
function normalizeContentType(contentType: string | undefined): string | undefined {
  return contentType?.toLowerCase();
}

// Component for displaying triggered policies
function TriggeredPoliciesInfo({ policies }: { policies: string[] }) {
  if (policies.length === 0) return null;

  return (
    <div className={styles.categoryMetric}>
      <span className={styles.categoryLabel}>Triggered by:</span>
      <div className={styles.policyBadgeContainer}>
        {policies.map(policyName => (
          <Badge key={policyName} size={BadgeSize.XSmall} text={policyName} textSize={BadgeTextSize.Small} />
        ))}
      </div>
    </div>
  );
}

function LabelDetails({ label, triggeredByPolicies }: { label: ClassificationLabel; triggeredByPolicies?: string[] }) {
  const normalizedType = normalizeContentType(label.content_type);
  const showSeverity = normalizedType === 'text' && label.severity !== undefined;
  const showScore = normalizedType === 'image' && label.score !== undefined;

  return (
    <div className={styles.categoryDetails}>
      {showSeverity && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Severity:</span>
          <span className={styles.severityValue}>{label.severity}</span>
        </div>
      )}
      {showScore && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Score:</span>
          <span className={styles.severityValue}>{label.score?.toFixed(2)}%</span>
        </div>
      )}
      {label.content_type && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Content Type:</span>
          <span className={styles.severityValue}>{label.content_type}</span>
        </div>
      )}
      {label.metadata && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Policy:</span>
          <span className={styles.severityValue}>{label.metadata.policy}</span>
        </div>
      )}
      {triggeredByPolicies && <TriggeredPoliciesInfo policies={triggeredByPolicies} />}
    </div>
  );
}

function LabelItem({
  label,
  isTriggeredByPolicy,
  triggeredByPolicies,
}: {
  label: ClassificationLabel;
  isTriggeredByPolicy: boolean;
  triggeredByPolicies?: string[];
}) {
  const styleClass = isTriggeredByPolicy ? styles.categoryItemUnsafe : styles.categoryItemSafe;

  return (
    <div className={`${styles.categoryItem} ${styleClass}`}>
      <div className={styles.categoryInfo}>
        <span className={styles.categoryName}>{label.label_name}</span>
        {label.label_short_name && label.label_short_name !== label.label_name && <span className={styles.categoryShortName}>({label.label_short_name})</span>}
      </div>
      <LabelDetails label={label} triggeredByPolicies={triggeredByPolicies} />
    </div>
  );
}

function buildPolicyMappings(policies: ClassificationResult['policies']) {
  const triggeredPolicyLabelIds = new Set<string>();
  const labelIdToPolicyNames = new Map<string, string[]>();

  if (policies && policies.length > 0) {
    policies.forEach(policy => {
      policy.labels.forEach(policyLabel => {
        triggeredPolicyLabelIds.add(policyLabel.label_id);
        const existingPolicies = labelIdToPolicyNames.get(policyLabel.label_id);

        if (existingPolicies) {
          labelIdToPolicyNames.set(policyLabel.label_id, [...existingPolicies, policy.name]);
        } else {
          labelIdToPolicyNames.set(policyLabel.label_id, [policy.name]);
        }
      });
    });
  }

  return { triggeredPolicyLabelIds, labelIdToPolicyNames };
}

function groupLabelsByContentType(labels: ClassificationLabel[]) {
  const grouped = labels.reduce(
    (acc, label) => {
      const contentType = normalizeContentType(label.content_type) || 'unknown';

      if (!acc[contentType]) {
        acc[contentType] = [];
      }

      acc[contentType].push(label);

      return acc;
    },
    {} as Record<string, ClassificationLabel[]>
  );

  const sortedTypes = Object.keys(grouped).sort((a, b) => {
    const order = { image: 0, text: 1, audio: 2, mixed: 3 };

    return (order[a as keyof typeof order] ?? 99) - (order[b as keyof typeof order] ?? 99);
  });

  return { grouped, sortedTypes };
}

function LabelsSection({ result }: { result: ClassificationResult }) {
  if (!result.labels || result.labels.length === 0) return null;

  const { triggeredPolicyLabelIds, labelIdToPolicyNames } = buildPolicyMappings(result.policies);
  const { grouped, sortedTypes } = groupLabelsByContentType(result.labels);

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Labels</h3>
      {sortedTypes.map(contentType => (
        <div key={contentType} className={styles.contentTypeGroup}>
          <div className={styles.contentTypeHeader}>Content Type: {contentType}</div>
          <div className={styles.categoriesList}>
            {grouped[contentType].map(label => {
              const isTriggeredByPolicy = label.label_id ? triggeredPolicyLabelIds.has(label.label_id) : false;
              const triggeredByPolicies = label.label_id ? labelIdToPolicyNames.get(label.label_id) : undefined;

              return (
                <LabelItem
                  key={label.label_id || label.label_name}
                  isTriggeredByPolicy={isTriggeredByPolicy}
                  label={label}
                  triggeredByPolicies={triggeredByPolicies}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function CategoriesSection({ result }: { result: ClassificationResult }) {
  if (!result.categories || result.categories.length === 0) return null;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Categories</h3>
      <div className={styles.categoriesList}>
        {result.categories.map(category => (
          <div key={category} className={styles.categoryItem}>
            <div className={styles.categoryInfo}>
              <span className={styles.categoryName}>{category}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function getScoreValue(contentType: ContentType, confidenceScore: number | undefined): number {
  if (confidenceScore === undefined) return 0;

  return contentType === ContentType.IMAGE ? confidenceScore : confidenceScore * 100;
}

function ConfidenceScoreDisplay({ result }: { result: ClassificationResult }) {
  if (result.confidenceScore === undefined) return null;

  // Don't display score for images
  if (result.contentType === ContentType.IMAGE) return null;

  const scoreValue = getScoreValue(result.contentType, result.confidenceScore);

  return (
    <div className={styles.metadataItemInline}>
      <span className={styles.metadataLabel}>Overall Confidence:</span>
      <span className={styles.metadataValue}>{scoreValue.toFixed(4)}%</span>
    </div>
  );
}

function LanguageDisplay({ result }: { result: ClassificationResult }) {
  const languageInfo = getLanguageDisplayInfo(result.languageDetected, result.languageClassified);

  if (languageInfo.showTranslationIndicator) {
    return (
      <>
        {result.languageDetected && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>Language Detected:</span>
            <span className={styles.metadataValue}>{getLanguageName(result.languageDetected)}</span>
          </div>
        )}
        {result.languageClassified && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>Language Classified:</span>
            <span className={styles.metadataValue}>{getLanguageName(result.languageClassified)}</span>
          </div>
        )}
      </>
    );
  }

  if (result.languageClassified || result.languageDetected) {
    return (
      <div className={styles.metadataItemInline}>
        <span className={styles.metadataLabel}>Language:</span>
        <span className={styles.metadataValue}>{getLanguageName(result.languageClassified || result.languageDetected)}</span>
      </div>
    );
  }

  return null;
}

function EnhancedMetadataSection({ result }: { result: ClassificationResult }) {
  const hasEnhancedData = result.confidenceScore !== undefined || result.language || result.languageDetected || result.duration;

  if (!hasEnhancedData) return null;

  const isImage = result.contentType === ContentType.IMAGE;

  return (
    <div className={styles.section}>
      {!isImage && <h3 className={styles.sectionTitle}>Classification Metadata</h3>}
      <div className={styles.metadataGrid}>
        <ConfidenceScoreDisplay result={result} />
        <LanguageDisplay result={result} />
        {result.duration && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>Duration:</span>
            <span className={styles.metadataValue}>{(result.duration / 1000).toFixed(2)}s</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ExplanationSection({ result }: { result: ClassificationResult }) {
  if (!result.explanation) return null;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Explanation</h3>
      <div className={styles.contentBox}>
        <span>{result.explanation}</span>
      </div>
    </div>
  );
}

/**
 * Dialog component for displaying classification result details
 */
export function ClassificationResultDialog({ result, isOpen, onClose, isSafe, isContentBlurred, onToggleBlur }: ClassificationResultDialogProps) {
  const dialogTitle = getDialogTitle(result.contentType);
  const dialogDescription = formatTimestamp(result.timestamp);

  const customHeader = (
    <div>
      <div className={styles.titleContainer}>
        <Icon icon={getContentTypeIcon(result.contentType)} iconSize={IconSize.Small} />
        <h2 className={styles.dialogTitle}>{dialogTitle}</h2>
        {!isSafe && (
          <IconButton
            buttonSize={IconButtonSize.Small}
            icon={isContentBlurred ? Eye : StatusCircleBlock}
            iconSize={IconSize.Small}
            title={isContentBlurred ? 'Show content' : 'Hide content'}
            onClick={onToggleBlur}
          />
        )}
      </div>
      {dialogDescription && <p className={styles.dialogDescription}>{dialogDescription}</p>}
    </div>
  );

  return (
    <Dialog cancelAction={{ label: 'Close' }} customHeader={customHeader} isOpen={isOpen} size="xl" onClose={onClose}>
      <div className={styles.statusBadge}>
        <Badge className={isSafe ? styles.safeBadge : styles.unsafeBadge} text={isSafe ? 'SAFE' : 'UNSAFE'} />
      </div>
      <div className={styles.container}>
        <ImageDisplaySection isContentBlurred={isContentBlurred} isSafe={isSafe} result={result} onToggleBlur={onToggleBlur} />
        <ContentDisplaySection isContentBlurred={isContentBlurred} isSafe={isSafe} result={result} onToggleBlur={onToggleBlur} />
        <ExplanationSection result={result} />
        <EnhancedMetadataSection result={result} />
        <CategoriesSection result={result} />
        <PoliciesSection result={result} />
        <LabelsSection result={result} />
      </div>
    </Dialog>
  );
}
